// src/context/UserContext.js
import React, { createContext, useState, useEffect, useRef } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInAnonymously,
  updateProfile,
} from "firebase/auth";
import {
  getDoc,
  setDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  runTransaction,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "../firebase";
import {sendCustomerVerificationEmail} from "../lib/customerEmailVerification";

export const UserContext = createContext();

/*───────────────────────────────────────────────
  Helpers (module scope)
───────────────────────────────────────────────*/
function resolveUid(candidate) {
  return typeof candidate === "string"
    ? candidate
    : candidate && typeof candidate.uid === "string"
    ? candidate.uid
    : null;
}

/* Normalize playlist entries (string ID or {id}) → unique array of IDs */
function toId(x) {
  return typeof x === "string" ? x : x?.id || null;
}
function normalizeIds(arr) {
  const seen = new Set(),
    out = [];
  for (const x of Array.isArray(arr) ? arr : []) {
    const id = toId(x);
    if (id && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

function moveItem(arr, fromIndex, toIndex) {
  if (!Array.isArray(arr)) return [];
  if (fromIndex === toIndex) return arr;
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= arr.length ||
    toIndex >= arr.length
  ) {
    return arr;
  }
  const next = arr.slice();
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}

/* Prefer fields on an object, else null (used when resolving titles) */
function pickTitle(obj) {
  const t =
    (obj &&
      (obj.title ||
        obj.name ||
        obj.songTitle ||
        obj.trackTitle ||
        obj.displayName)) ||
    null;
  return t && String(t).trim() ? String(t).trim() : null;
}

/* Basic non-empty string check */
function nonEmptyStr(s) {
  return typeof s === "string" && s.trim().length > 0;
}

/* Still available if you pass whole track objects around */
function getTrackTitle(x) {
  const t =
    (x && (x.title || x.name || x.songTitle || x.trackTitle || x.displayName)) ||
    "Track";
  return String(t).trim();
}

/*───────────────────────────────────────────────
  Provider
───────────────────────────────────────────────*/
export function UserProvider({ children }) {
  /* ───── state ───── */
  const [user, setUser] = useState(null); // null ⇒ guest / anonymous
  const [playlist, setPlaylist] = useState([]);
  const [likedTracksSet, setLikedTracksSet] = useState(new Set());
  const [likeCountDeltas, setLikeCountDeltas] = useState(new Map());

  /** During in-flight like/playlist ops (guards UI duplicates) */
  const pendingLikesRef = useRef(new Set());
  const pendingPlaylistAddsRef = useRef(new Set());

  const hadUser = useRef(false);

  /** Cache for track titles (so we don't fetch per click) */
  const titleCacheRef = useRef(new Map());

  /* Resolve a friendly title for alerts: object → cache → Firestore */
  async function resolveTrackTitleForAlerts(id, maybeTrack) {
    const fromObj = pickTitle(maybeTrack);
    if (fromObj) return fromObj;

    if (titleCacheRef.current.has(id)) return titleCacheRef.current.get(id);

    try {
      const s = await getDoc(doc(db, "tracks", id));
      const title = s.exists() ? pickTitle(s.data()) || "Track" : "Track";
      titleCacheRef.current.set(id, title);
      return title;
    } catch {
      return "Track";
    }
  }

  /* ────────────────────────────────────────────────
        HELPERS
  ────────────────────────────────────────────────*/
  async function writeUserDoc(uidOrObj, patch) {
    const uid = resolveUid(uidOrObj);
    if (!uid) return; // nothing to do when signed-out / malformed

    await setDoc(
      doc(db, "users", uid),
      { owner: uid, ...patch },
      { merge: true }
    );
  }

  async function savePlaylistToFirestore(newList) {
    const uid = resolveUid(user);
    if (!uid) return;
    await writeUserDoc(uid, {
      playlist: newList,
      updatedAt: serverTimestamp(),
    });
  }

  /* ────────────────────────────────────────────────
      AUTH STATE LISTENER  (no anon fallback)
  ────────────────────────────────────────────────*/
  useEffect(() => {
    let unsubUserDoc = () => {};

    /* heavy extras loaded after first paint */
    const loadExtras = async (fbUser) => {
      const userRef = doc(db, "users", fbUser.uid);
      const snap = await getDoc(userRef);
      const profileData = snap.exists() ? snap.data() : {};

      // just normalize shapes – DO NOT check tracks/ or write back here
      const normalized = normalizeIds(profileData.playlist);
      setPlaylist(normalized);

      /* live liked-tracks + playlist sync */
      unsubUserDoc = onSnapshot(userRef, (s) => {
        const d = s.exists() ? s.data() : {};
        const srvLikes = Array.isArray(d.likedTracks) ? d.likedTracks : [];

        setLikedTracksSet((prev) => {
          const merged = new Set(prev);
          pendingLikesRef.current.forEach((id) => merged.add(id));
          srvLikes.forEach((id) => merged.add(id));
          return merged;
        });

        // always normalize incoming playlist from the user doc
        setPlaylist(normalizeIds(d.playlist));
      });

      /* merge any extra profile fields for UI */
      setUser((u) => ({
        ...u,
        displayName: profileData.displayName ?? fbUser.displayName,
      }));
    };

    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      /* treat anonymous as signed-out */
      if (!fbUser || fbUser.isAnonymous) {
        if (hadUser.current) {
          setUser(null);
          setPlaylist([]);
        }
        // stop listening to user doc when signed-out
        unsubUserDoc();
        unsubUserDoc = () => {};
        return;
      }
      hadUser.current = true;

      /* Ensure profile doc exists without clobbering fields */
      const userRef = doc(db, "users", fbUser.uid);
      let profileData = {};
      let exists = false;

      try {
        const s = await getDoc(userRef);
        exists = s.exists();
        profileData = exists ? s.data() || {} : {};
      } catch {
        /* ignore */
      }

      // Build a safe patch:
      // - never write displayName unless we have a non-empty auth value
      // - only set createdAt the first time
      const patch = {
        owner: fbUser.uid,
        email: fbUser.email ?? null,
      };
      if (!exists || !profileData.createdAt) {
        patch.createdAt = serverTimestamp();
      }
      const authName = (fbUser.displayName || "").trim();
      if (nonEmptyStr(authName)) {
        patch.displayName = authName;
      }

      await setDoc(userRef, patch, { merge: true });

      /* Optionally backfill Firebase Auth displayName from Firestore
         (so future logins also carry it on the auth user object) */
      if (!nonEmptyStr(authName) && nonEmptyStr(profileData.displayName)) {
        try {
          await updateProfile(fbUser, { displayName: profileData.displayName });
        } catch {
          /* non-fatal */
        }
      }

      /* Instant commit — prefer Firestore name if present */
      setUser({
        uid: fbUser.uid,
        email: fbUser.email,
        displayName: profileData.displayName ?? fbUser.displayName ?? null,
        isAdmin: !!profileData.isAdmin,
        isAnonymous: !!fbUser.isAnonymous,
      });

      /* async extras */
      loadExtras(fbUser);
    });

    /* cleanup for the effect (outside the callback!) */
    return () => {
      unsub();
      unsubUserDoc();
    };
  }, []);

  /* ────────────────────────────────────────────────
        AUTH HELPERS
  ────────────────────────────────────────────────*/
  async function signUp(displayName, email, password) {
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (nonEmptyStr(displayName)) {
        await updateProfile(cred.user, { displayName: displayName.trim() });
      }
      const patch = {
        owner: cred.user.uid,
        email,
        createdAt: serverTimestamp(),
      };
      if (nonEmptyStr(displayName)) patch.displayName = displayName.trim();

      await setDoc(doc(db, "users", cred.user.uid), patch, { merge: true });
      let verificationSent = false;
      try {
        const verification = await sendCustomerVerificationEmail(cred.user);
        verificationSent = verification.sent || verification.alreadyVerified;
      } catch (verificationError) {
        // The account already exists at this point. Return a recoverable state so
        // the UI never encourages a duplicate signup attempt.
        return { success: true, verificationSent: false, verificationRequired: true,
          verificationError: verificationError?.code || "verification-email-send-failed" };
      }
      return { success: true, verificationSent, verificationRequired: !cred.user.emailVerified };
    } catch (error) {
      console.error("signUp error:", error);
      return { success: false, error: error.message };
    }
  }

  async function signIn(email, password) {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return { success: true };
    } catch (error) {
      console.error("signIn error:", error);
      return { success: false, error: error.message };
    }
  }

  async function logOut() {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      console.error("logOut error:", error);
      return { success: false, error: error.message };
    }
  }

  /* ────────────────────────────────────────────────
        PLAYLIST HELPERS
  ────────────────────────────────────────────────*/
  async function addTrackToPlaylist(track) {
    const uid = resolveUid(user);
    if (!uid || user?.isAnonymous) {
      alert("Please sign in to add to playlist");
      return;
    }

    const id = toId(track);
    if (!id) return;

    // Resolve a nice title (object → cache → Firestore)
    const title = await resolveTrackTitleForAlerts(id, track);

    // Fast local guard
    if (playlist.includes(id)) {
      alert(
        `Hey ${user?.displayName || "fam"}, ${title} is already in your playlist, why don't you just buy it?`
      );
      return;
    }

    // In-flight guard to avoid racey double alerts
    if (pendingPlaylistAddsRef.current.has(id)) {
      alert(
        `Hey ${user?.displayName || "fam"}, ${title} is already in your playlist, why don't you just buy it?`
      );
      return;
    }
    pendingPlaylistAddsRef.current.add(id);

    // Optimistic UI
    let optimisticallyAdded = false;
    setPlaylist((prev) => {
      if (prev.includes(id)) return prev;
      optimisticallyAdded = true;
      return [...prev, id];
    });

    const userRef = doc(db, "users", uid);
    let existedOnServer = false;

    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(userRef);
        const serverList = normalizeIds(
          snap.exists() ? snap.data().playlist || [] : []
        );

        if (serverList.includes(id)) {
          existedOnServer = true;
          tx.set(userRef, { updatedAt: serverTimestamp() }, { merge: true });
        } else {
          tx.set(
            userRef,
            { owner: uid, playlist: arrayUnion(id), updatedAt: serverTimestamp() },
            { merge: true }
          );
        }
      });

      if (existedOnServer) {
        setPlaylist((prev) => (prev.includes(id) ? prev : [...prev, id]));
        alert(
          `Hey ${user?.displayName || "fam"}, ${title} is already in your playlist, why don't you just buy it?`
        );
      } else {
        alert(`${title} added to playlist`);
      }
    } catch (e) {
      console.error("addTrackToPlaylist error:", e);
      // Roll back optimistic add if we added it locally
      if (optimisticallyAdded) {
        setPlaylist((prev) => prev.filter((x) => x !== id));
      }
      alert("Sorry, couldn't save your playlist. Please try again.");
    } finally {
      pendingPlaylistAddsRef.current.delete(id);
    }
  }

  async function removeTrackFromPlaylist(trackIdOrObj) {
    const uid = resolveUid(user);
    if (!uid || user?.isAnonymous) return;
    const id = toId(trackIdOrObj);
    if (!id) return;

    // optimistic UI
    setPlaylist((prev) => prev.filter((pid) => pid !== id));

    try {
      await setDoc(
        doc(db, "users", uid),
        { playlist: arrayRemove(id), updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (e) {
      console.error("removeTrackFromPlaylist error:", e);
      // optional: re-add on failure
      setPlaylist((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }
  }

  async function clearPlaylist() {
    if (!user) return;
    setPlaylist([]);
    await savePlaylistToFirestore([]);
  }

  async function reorderPlaylist(nextOrderIds = []) {
    const uid = resolveUid(user);
    if (!uid || user?.isAnonymous) return;

    const normalized = normalizeIds(nextOrderIds);
    let previous = null;
    let changed = false;

    setPlaylist((prev) => {
      previous = prev;
      changed =
        normalized.length !== prev.length ||
        normalized.some((id, i) => id !== prev[i]);
      return changed ? normalized : prev;
    });

    if (!changed) return;

    try {
      await savePlaylistToFirestore(normalized);
    } catch (e) {
      console.error("reorderPlaylist error:", e);
      if (previous) setPlaylist(previous);
    }
  }

  async function moveTrackInPlaylist(trackIdOrObj, direction = 1) {
    const uid = resolveUid(user);
    if (!uid || user?.isAnonymous) return;
    const id = toId(trackIdOrObj);
    if (!id) return;

    let previousList = null;
    let nextList = null;

    setPlaylist((prev) => {
      previousList = prev;
      const fromIndex = prev.findIndex((pid) => pid === id);
      if (fromIndex < 0) {
        nextList = prev;
        return prev;
      }

      const step = direction < 0 ? -1 : 1;
      const toIndex = fromIndex + step;
      nextList = moveItem(prev, fromIndex, toIndex);
      return nextList;
    });

    if (!nextList || nextList === previousList) return;

    try {
      await savePlaylistToFirestore(nextList);
    } catch (e) {
      console.error("moveTrackInPlaylist error:", e);
      if (previousList) setPlaylist(previousList);
    }
  }

    /* ────────────────────────────────────────────────
     NEW: Like-count helpers (global optimistic state)
  ────────────────────────────────────────────────*/
  function bumpLikeDelta(trackId, by = 1) {
    if (!trackId) return;
    setLikeCountDeltas((prev) => {
      const next = new Map(prev);
      next.set(trackId, (next.get(trackId) || 0) + by);
      return next;
    });
  }

  /** Public helper: compute the *live* like count for a track */
  function getLikeCount(trackId, baseLikeCount = 0) {
    const delta = likeCountDeltas.get(trackId) || 0;
    return (baseLikeCount || 0) + delta;
  }

   /* ────────────────────────────────────────────────
     LIKE HELPERS — now with optimistic count
  ────────────────────────────────────────────────*/
  async function handleLike(track) {
    if (!user) {
      alert("Please sign in to like tracks.");
      return { alreadyLiked: false };
    }

    const id = track?.id;
    if (!id) return { alreadyLiked: false };

    // Client guard & in-flight guard
    if (likedTracksSet.has(id) || pendingLikesRef.current.has(id)) {
      return { alreadyLiked: true };
    }

    // Mark in-flight and optimistic "liked" flag
    pendingLikesRef.current.add(id);
    setLikedTracksSet((prev) => new Set(prev).add(id));

    // NEW: optimistic like-count bump (visible instantly everywhere)
    bumpLikeDelta(id, +1);

    try {
      const recordTrackLike = httpsCallable(functions, "recordTrackLikeV1");
      const response = await recordTrackLike({trackId: id});
      if (response?.data?.alreadyLiked) throw new Error("already-liked");

      // success: keep optimistic delta and liked flag
      pendingLikesRef.current.delete(id);
      return { alreadyLiked: false };
    } catch (err) {
      // Roll back optimistic liked flag & delta
      pendingLikesRef.current.delete(id);
      setLikedTracksSet((prev) => {
        const s = new Set(prev);
        s.delete(id);
        return s;
      });
      bumpLikeDelta(id, -1); // undo the +1

      if (err.message === "already-liked") {
        // treat as duplicate (no alert here; caller can handle)
        return { alreadyLiked: true };
      }
      console.error("handleLike error:", err);
      return { alreadyLiked: false };
    }
  }


  /* ────────────────────────────────────────────────
        CONTEXT VALUE
  ────────────────────────────────────────────────*/
  return (
    <UserContext.Provider
      value={{
        user,
        isAdmin: user?.isAdmin || false,
        playlist,
        likedTracksSet,
        likeCountDeltas,
        getLikeCount,
        signUp,
        signIn,
        logOut,
        handleLike,
        addTrackToPlaylist,
        removeTrackFromPlaylist,
        clearPlaylist,
        reorderPlaylist,
        moveTrackInPlaylist,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}
